import {
  Money,
  TaxRate,
  TaxStrategy,
  getTaxRateByRegion,
  roundTo2Decimals,
} from '@principle-theorem/accounting';
import {
  IDestinationEntityJobRunOptions,
  type FailedDestinationEntityRecord,
  type IDestinationEntity,
  type IDestinationEntityRecord,
  type IGetRecordResponse,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  AccountCredit,
  Invoice,
  LineItemActions,
  Patient,
  Transaction,
  TransactionOperators,
  depositToLineItem,
  toAccountDetails,
} from '@principle-theorem/principle-core';
import {
  AccountCreditType,
  InvoiceStatus,
  TransactionProvider,
  TransactionStatus,
  TransactionType,
  type IBasePatient,
  type IFeeLineItem,
  type IPatient,
  type IPatientContactDetails,
  type IPractice,
  type ITransaction,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  getError,
  resolveSequentially,
  snapshotCombineLatest,
  sortByCreatedAt,
  toNamedDocument,
  type DocumentReference,
  type IIdentifiable,
  type Timestamp,
  type WithRef,
} from '@principle-theorem/shared';
import { Observable, combineLatest, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { PATIENT_RESOURCE_TYPE } from '../../../destination/entities/patient';
import {
  BasePatientDepositDestinationEntity,
  IDepositBuildData,
  IDepositJobData,
  IDepositMigrationData,
} from '../../../destination/entities/patient-deposits';
import { getConfigurationItem } from '../../../source/source';
import { type TranslationMapHandler } from '../../../translation-map';
import {
  PraktikaTransactionEffect,
  PraktikaTransationType,
} from '../../source/entities/appointment-invoice-payment';
import {
  PatientSourceEntity,
  PraktikaPatient,
} from '../../source/entities/patient';
import { PatientAppointmentSourceEntity } from '../../source/entities/patient-appointment';
import {
  PatientDepositAdjustmentSourceEntity,
  PraktikaDepositAdjustment,
} from '../../source/entities/patient-deposit-adjustments';
import {
  PatientDepositPaymentSourceEntity,
  PraktikaDepositPayment,
} from '../../source/entities/patient-deposit-payments';
import { PraktikaPracticeMappingHandler } from '../mappings/practices';
import { PraktikaStafferMappingHandler } from '../mappings/staff';
import { PraktikaSurcharge } from './lib/praktika-surcharge';
import { PatientDestinationEntity } from './patients';
import { StafferDestinationEntity } from './staff';

interface IDepositData {
  id: string;
  number: string;
  date: Timestamp;
  patientId: string;
  payments: IGetRecordResponse<PraktikaDepositPayment>[];
  adjustments: IGetRecordResponse<PraktikaDepositAdjustment>[];
}

export class PatientDepositDestinationEntity extends BasePatientDepositDestinationEntity<
  PraktikaPatient,
  IDepositJobData<PraktikaPatient>
> {
  patientSourceEntity = new PatientSourceEntity();

  override sourceEntities = {
    patients: new PatientSourceEntity(),
    depositPayments: new PatientDepositPaymentSourceEntity(),
    depositAdjustments: new PatientDepositAdjustmentSourceEntity(),
    appointments: new PatientAppointmentSourceEntity(),
  };

  override destinationEntities = {
    patients: new PatientDestinationEntity(),
    staff: new StafferDestinationEntity(),
  };

  customMappings = {
    staff: new PraktikaStafferMappingHandler(),
    practices: new PraktikaPracticeMappingHandler(),
  };

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<IDepositJobData<PraktikaPatient>[]> {
    const practices$ = this.customMappings.practices.getRecords(translationMap);

    return snapshotCombineLatest([practices$]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.patientSourceEntity,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(([sourcePatients, [practices]]) =>
        sourcePatients.map((sourcePatient) => ({
          sourcePatient,
          practices,
        }))
      )
    );
  }

  async buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: IDepositJobData<PraktikaPatient>
  ): Promise<
    | IDepositMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
  > {
    const sourcePatientId = data.sourcePatient.data.patient_id.toString();

    const payments = await this.sourceEntities.depositPayments.filterRecords(
      migration,
      'patientId',
      sourcePatientId
    );

    const adjustments =
      await this.sourceEntities.depositAdjustments.filterRecords(
        migration,
        'patientId',
        sourcePatientId
      );

    const deposits: IDepositData[] = [];

    payments.map((payment) => {
      const depositFound = deposits.find(
        (deposit) => deposit.id === payment.data.deposit_id.toString()
      );
      if (depositFound) {
        if (
          depositFound.date.seconds >
          payment.record.translations.effectiveDate.seconds
        ) {
          depositFound.date = payment.record.translations.effectiveDate;
        }
        depositFound.payments.push(payment);
        return;
      }
      deposits.push({
        id: payment.data.deposit_id.toString(),
        number: payment.data.deposit_number.toString(),
        date: payment.record.translations.effectiveDate,
        patientId: sourcePatientId,
        adjustments: [],
        payments: [payment],
      });
    });

    adjustments.map((adjustment) => {
      const depositFound = deposits.find(
        (deposit) => deposit.id === adjustment.data.deposit_id.toString()
      );
      if (depositFound) {
        if (
          depositFound.date.seconds >
          adjustment.record.translations.effectiveDate.seconds
        ) {
          depositFound.date = adjustment.record.translations.effectiveDate;
        }
        depositFound.adjustments.push(adjustment);
        return;
      }
      deposits.push({
        id: adjustment.data.deposit_id.toString(),
        number: adjustment.data.deposit_number.toString(),
        date: adjustment.record.translations.effectiveDate,
        patientId: sourcePatientId,
        adjustments: [adjustment],
        payments: [],
      });
    });

    const patientRef = await translationMap.getDestination<IPatient>(
      data.sourcePatient.data.patient_id.toString(),
      PATIENT_RESOURCE_TYPE
    );

    const practiceMap = await this.customMappings.practices.getBySource(
      getConfigurationItem(migration.source, 'practice id', ''),
      translationMap
    );

    if (!patientRef || !practiceMap?.destinationIdentifier) {
      const message: string[] = [];
      if (!patientRef) {
        message.push('No patient');
      }
      if (!practiceMap?.destinationIdentifier) {
        message.push('No practice has been mapped');
      }

      return this._buildErrorResponse(data.sourcePatient, message.join('; '));
    }

    const organisation = await Firestore.getDoc(
      migration.configuration.organisation.ref
    );
    const taxRate = getTaxRateByRegion(organisation.region);

    try {
      const builtDeposits = await resolveSequentially(
        deposits,
        async (deposit) => {
          if (!practiceMap.destinationIdentifier) {
            throw new Error('No practice has been mapped');
          }
          const depositData = await this._buildDepositData(
            deposit,
            practiceMap.destinationIdentifier,
            patientRef,
            taxRate
          );

          if (!depositData) {
            throw new Error(`Deposit ${deposit.id} has no amount`);
          }

          return depositData;
        }
      );

      return {
        patientRef,
        deposits: builtDeposits,
      };
    } catch (error) {
      return this._buildErrorResponse(data.sourcePatient, getError(error));
    }
  }

  private async _buildDepositData(
    deposit: IDepositData,
    practiceRef: DocumentReference<IPractice>,
    patientRef: DocumentReference<IPatient>,
    taxRate: TaxRate
  ): Promise<IDepositBuildData | undefined> {
    const practice = await Firestore.getDoc(practiceRef);
    const resolvedPatient = await Firestore.getDoc(patientRef);
    const primaryContact = await Patient.resolvePrimaryContact(resolvedPatient);
    const onBehalfOf = toAccountDetails(
      resolvedPatient as IBasePatient & IPatientContactDetails
    );

    const transactions = getTransactions(deposit.payments, practiceRef);
    const used = Invoice.balance(
      { items: [] },
      getAdjustments(deposit.adjustments, practiceRef)
    );

    const surcharges = getSurcharges(deposit.adjustments, taxRate);
    const paid = Math.abs(Invoice.balance({ items: [] }, transactions));
    const amount = roundTo2Decimals(paid - LineItemActions.sum(surcharges));

    const invoice = Invoice.init({
      from: toAccountDetails(practice),
      to: primaryContact
        ? {
            ...toAccountDetails(primaryContact),
            onBehalfOf,
          }
        : onBehalfOf,
      practice: toNamedDocument(practice),
      items: [depositToLineItem('Deposit', amount), ...surcharges],
      createdAt: deposit.date,
    });

    const status = getDepositInvoiceStatus(amount);
    if (status === InvoiceStatus.Cancelled) {
      throw new Error('Deposit has no amount');
    }

    const lastTransaction = new TransactionOperators(transactions)
      .completed()
      .sort(sortByCreatedAt)
      .reverse()
      .last();

    if (status === InvoiceStatus.Paid && lastTransaction) {
      invoice.paidAt = lastTransaction.createdAt;
    }

    const issuedAt = lastTransaction?.createdAt ?? deposit.date;
    invoice.issuedAt = issuedAt;
    invoice.reference = deposit.number;
    Invoice.updateStatus(invoice, status, issuedAt);

    const accountCredit = {
      ...AccountCredit.init({
        type: AccountCreditType.Deposit,
        description: 'Deposit',
        amount,
        used: roundTo2Decimals(used),
        practiceRef: practice.ref,
      }),
      createdAt: issuedAt,
    };

    return {
      uid: deposit.id,
      invoice,
      transactions,
      accountCredit,
    };
  }
}

// TODO: throw if cancelled and inspect the data
function getDepositInvoiceStatus(transactionAmount: number): InvoiceStatus {
  return transactionAmount > 0 ? InvoiceStatus.Paid : InvoiceStatus.Cancelled;
}

function getTransactions(
  depositPayments: IGetRecordResponse<PraktikaDepositPayment>[],
  practiceRef: DocumentReference<IPractice>
): (IIdentifiable & ITransaction)[] {
  return depositPayments.map((paymentRecord) => {
    const payment = paymentRecord.data;
    const uid = payment.id.toString();
    return {
      ...Transaction.init({
        ...Transaction.internalReference(TransactionProvider.Manual, uid),
        type:
          payment.effect === PraktikaTransactionEffect.Cr
            ? TransactionType.Incoming
            : TransactionType.Outgoing,
        status: TransactionStatus.Complete,
        from: '',
        to: '',
        amount: Math.abs(Money.fromCents(payment.amount)),
        extendedData: payment,
        createdAt: paymentRecord.record.translations.effectiveDate,
        practiceRef,
      }),
    };
  });
}

/**
 * A surcharge only attracts GST when the transaction it was raised against
 * does. Deposits are always GST free, so their surcharges are too.
 */
function getSurcharges(
  depositAdjustments: IGetRecordResponse<PraktikaDepositAdjustment>[],
  taxRate: TaxRate
): IFeeLineItem[] {
  return depositAdjustments
    .filter((adjustmentRecord) =>
      PraktikaSurcharge.isSurcharge(adjustmentRecord.data)
    )
    .map((adjustmentRecord) =>
      PraktikaSurcharge.toFeeLineItem(
        adjustmentRecord.data,
        taxRate,
        TaxStrategy.GSTFree
      )
    );
}

function getAdjustments(
  depositAdjustments: IGetRecordResponse<PraktikaDepositAdjustment>[],
  practiceRef: DocumentReference<IPractice>
): (IIdentifiable & ITransaction)[] {
  return depositAdjustments
    .filter(
      (adjustment) =>
        adjustment.data.type_id === PraktikaTransationType.TransferFromDepositDr
    )
    .map((adjustmentRecord) => {
      const adjustment = adjustmentRecord.data;
      const uid = adjustment.id.toString();
      // TODO: Filter out the use of deposits
      return Transaction.init({
        ...Transaction.internalReference(TransactionProvider.Discount, uid),
        type: TransactionType.Outgoing,
        status: TransactionStatus.Complete,
        from: '',
        to: '',
        amount: Math.abs(Money.fromCents(adjustment.amount)),
        extendedData: adjustment,
        createdAt: adjustmentRecord.record.translations.effectiveDate,
        practiceRef,
      });
    });
}
